import React from 'react';
import { Box, Typography, Button, Paper, Stack } from '@mui/material';
import { Refresh as RefreshIcon, Home as HomeIcon, BugReport as BugReportIcon } from '@mui/icons-material';

/**
 * Parse component stack to extract meaningful component names
 */
function parseComponentStack(componentStack) {
  if (!componentStack) return [];
  
  // Extract component names from the stack
  const lines = componentStack.split('\n').filter(line => line.trim());
  const components = [];
  
  for (const line of lines) {
    // Match patterns like "at ComponentName" or "at ComponentName (file.js:line:col)"
    const match = line.match(/at\s+([A-Z][a-zA-Z0-9_]*)/);
    if (match && match[1]) {
      components.push(match[1]);
    }
  }
  
  return components;
}

/**
 * Extract route/page info from URL
 */
function extractRouteInfo(url) {
  try {
    const urlObj = new URL(url);
    const pathname = urlObj.pathname;
    
    // Extract meaningful route segments
    const segments = pathname.split('/').filter(Boolean);
    
    // Try to identify the page type
    if (segments.includes('projects')) {
      const projectIndex = segments.indexOf('projects');
      const projectId = segments[projectIndex + 1];
      const section = segments[projectIndex + 2] || 'overview';
      return {
        page: 'ProjectBuilder',
        projectId: projectId || 'unknown',
        section: section,
        fullPath: pathname
      };
    }
    
    if (segments.includes('dashboard')) {
      return { page: 'Dashboard', fullPath: pathname };
    }
    
    if (segments.includes('rarity')) {
      return { page: 'RarityAnalysis', fullPath: pathname };
    }
    
    return { page: segments[0] || 'Home', fullPath: pathname };
  } catch {
    return { page: 'Unknown', fullPath: url };
  }
}

/**
 * Send error notification to admin via the GraveForge API server
 */
async function sendErrorNotification(error, errorInfo, userAgent, url, errorId) {
  try {
    const serverUrl = import.meta.env.VITE_SERVER_URL;
    if (!serverUrl) {
      console.warn('⚠️ VITE_SERVER_URL not configured, skipping error notification');
      return;
    }

    // Parse component stack to get meaningful component names
    const componentNames = parseComponentStack(errorInfo?.componentStack);
    const routeInfo = extractRouteInfo(url || window.location.href);
    
    // Try to identify the likely source component
    const likelySource = componentNames.length > 0 
      ? `Component: ${componentNames[0]} (in ${componentNames.slice(0, 3).join(' → ')})`
      : 'Unknown component';

    const errorData = {
      errorMessage: error?.message || error?.toString() || 'Unknown error',
      errorName: error?.name || 'Error',
      errorStack: error?.stack || 'No stack trace available',
      componentStack: errorInfo?.componentStack || 'No component stack available',
      url: url || window.location.href,
      userAgent: userAgent || navigator.userAgent,
      timestamp: new Date().toISOString(),
      environment: import.meta.env.MODE || 'unknown',
      // Error reference ID (shown to user for support correlation)
      errorId: errorId || 'N/A',
      // Enhanced debugging info
      appVersion: import.meta.env.VITE_APP_VERSION || 'unknown',
      buildTime: import.meta.env.VITE_BUILD_TIME || 'unknown',
      routeInfo: JSON.stringify(routeInfo),
      componentPath: componentNames.join(' → '),
      likelySource: likelySource,
      // Browser/device info
      screenSize: `${window.innerWidth}x${window.innerHeight}`,
      language: navigator.language,
      platform: navigator.platform
    };

    console.error('🚨 Error caught by ErrorBoundary:');
    console.error('  Message:', errorData.errorMessage);
    console.error('  Likely Source:', likelySource);
    console.error('  Route:', routeInfo.page, routeInfo.section || '');
    console.error('  Component Path:', errorData.componentPath);
    console.error('📧 Sending error notification to admin...');

    const response = await fetch(`${serverUrl}/graveforge/auth/error-notification`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(errorData)
    });

    if (response.ok) {
      console.error('✅ Error notification sent successfully');
    } else {
      const errorText = await response.text();
      console.error('❌ Failed to send error notification:', response.status, errorText);
    }
  } catch (notifyError) {
    // Don't throw - we don't want notification failures to cause more errors
    console.error('❌ Error sending notification:', notifyError.message);
  }
}

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null,
      notificationSent: false,
      errorId: null
    };
  }

  static getDerivedStateFromError(error) {
    // Generate a unique error ID for this error instance
    const errorId = Date.now().toString(36).toUpperCase();
    return { hasError: true, errorId };
  }

  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    this.setState({
      error,
      errorInfo
    });

    // Send error notification email (only once per error)
    if (!this.state.notificationSent) {
      this.setState({ notificationSent: true });
      sendErrorNotification(
        error,
        errorInfo,
        navigator.userAgent,
        window.location.href,
        this.state.errorId // Pass the error ID to include in the email
      );
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <Box sx={{ 
          minHeight: '100vh',
          bgcolor: '#0a0a0a',
          backgroundImage: 'radial-gradient(ellipse at top, rgba(139, 0, 0, 0.15) 0%, transparent 50%)',
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          p: 3
        }}>
          <Paper sx={{ 
            p: 4, 
            maxWidth: 700,
            width: '100%',
            bgcolor: '#1a1a1a',
            border: '1px solid rgba(39, 48, 63, 0.6)',
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)'
          }}>
            {/* Logo Header */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <img 
                src="https://images.solanadeads.com/graveforge.png"
                alt="GraveForge Logo" 
                style={{ 
                  maxWidth: '180px', 
                  height: 'auto',
                  marginBottom: '16px'
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
              <Typography 
                variant="h4" 
                sx={{ 
                  color: '#ff4444',
                  fontWeight: 700,
                  textShadow: '0 0 20px rgba(255, 68, 68, 0.5)'
                }}
              >
                Something went wrong
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: 'rgba(255, 255, 255, 0.6)',
                  mt: 1
                }}
              >
                Our team has been notified and is looking into this issue.
              </Typography>
            </Box>

            {/* User-friendly error message - no technical details exposed */}
            <Box sx={{ 
              bgcolor: 'rgba(39, 48, 63, 0.4)',
              border: '1px solid rgba(39, 48, 63, 0.5)',
              borderRadius: 2,
              p: 3,
              mb: 3,
              textAlign: 'center'
            }}>
              <BugReportIcon sx={{ color: '#ef4444', fontSize: 40, mb: 1 }} />
              <Typography 
                variant="body1" 
                sx={{ 
                  color: 'rgba(255, 255, 255, 0.9)',
                  mb: 1
                }}
              >
                An unexpected error occurred while loading this page.
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: 'rgba(255, 255, 255, 0.6)'
                }}
              >
                Don't worry - our team has been automatically notified and will investigate this issue.
              </Typography>
            </Box>

            {/* Error reference ID for support */}
            <Box sx={{ 
              bgcolor: 'rgba(255, 255, 255, 0.05)',
              borderRadius: 1,
              p: 1.5,
              mb: 3,
              textAlign: 'center'
            }}>
              <Typography 
                variant="caption" 
                sx={{ 
                  color: 'rgba(255, 255, 255, 0.5)',
                  fontFamily: 'monospace'
                }}
              >
                Error ID: {this.state.errorId}
              </Typography>
            </Box>

            {/* Action Buttons */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Button 
                variant="contained" 
                onClick={() => window.location.reload()}
                startIcon={<RefreshIcon />}
                sx={{ 
                  flex: 1,
                  py: 1.5,
                  bgcolor: '#991b1b',
                  color: '#fff',
                  fontWeight: 600,
                  '&:hover': {
                    bgcolor: '#ef4444',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 12px rgba(204, 0, 0, 0.4)'
                  },
                  transition: 'all 0.2s ease'
                }}
              >
                Reload Page
              </Button>
              <Button 
                variant="outlined" 
                onClick={() => window.location.href = '/'}
                startIcon={<HomeIcon />}
                sx={{ 
                  flex: 1,
                  py: 1.5,
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                  color: 'rgba(255, 255, 255, 0.8)',
                  fontWeight: 600,
                  '&:hover': {
                    borderColor: 'rgba(255, 255, 255, 0.6)',
                    bgcolor: 'rgba(255, 255, 255, 0.05)',
                  }
                }}
              >
                Go Home
              </Button>
            </Stack>

            {/* Footer */}
            <Typography 
              variant="caption" 
              sx={{ 
                display: 'block',
                textAlign: 'center',
                color: 'rgba(255, 255, 255, 0.4)',
                mt: 3,
                pt: 2,
                borderTop: '1px solid rgba(255, 255, 255, 0.1)'
              }}
            >
              If this problem persists, please contact support at{' '}
              <a 
                href="mailto:support@solanadeads.com" 
                style={{ color: '#ef4444', textDecoration: 'none' }}
              >
                support@solanadeads.com
              </a>
            </Typography>
          </Paper>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
